import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import LoyaltyPointService from '../services/LoyaltyPointService';
import { RootState } from './store';
import { ILoyaltyPoint, ILoyaltyPointRequest } from "@ontime/types";

// --- LocalStorage Helpers ---
const loadState = (): LoyaltyPointState => {
    try {
        const serializedState = localStorage.getItem('loyaltyPoints');
        return serializedState ? JSON.parse(serializedState) : { loyaltyPoints: [], loading: false, error: null };
    } catch (error) {
        console.error('Could not load service providers state:', error);
        return { loyaltyPoints: [], loading: false, error: null };
    }
};

const saveState = (state: LoyaltyPointState) => {
    try {
        localStorage.setItem('loyaltyPoints', JSON.stringify(state));
    } catch (error) {
        console.error('Could not save service providers state:', error);
    }
};

// --- Async Thunks ---
export const fetchLoyaltyPoints = createAsyncThunk(
    'loyaltyPoints/fetchAll',
    async ({ organizationId, showDeleted }: { organizationId: string, showDeleted: boolean }) => {
        try {
            return await LoyaltyPointService.GetList(organizationId, showDeleted);
        } catch (error) {
            return null;
        }
    }
);

export const getLoyaltyPointAsync = createAsyncThunk(
    'loyaltyPoints/get',
    async ({ organizationId, loyaltyPointId }: { organizationId: string; loyaltyPointId: string }) => {
        try {
            return await LoyaltyPointService.Get(organizationId, loyaltyPointId);
        } catch (error) {
            return null;
        }
    }
);

export const createLoyaltyPointAsync = createAsyncThunk(
    'loyaltyPoints/create',
    async ({ organizationId, data }: { organizationId: string; data: ILoyaltyPointRequest }) => {
        try {
            return await LoyaltyPointService.Create(organizationId, data);
        } catch (error) {
            return null;
        }
    }
);

export const updateLoyaltyPointAsync = createAsyncThunk(
    'loyaltyPoints/update',
    async ({ organizationId, data }: { organizationId: string; data: ILoyaltyPointRequest }) => {
        try {
            return await LoyaltyPointService.Update(organizationId, data);
        } catch (error) {
            return null;
        }
    }
);

export const deleteLoyaltyPointAsync = createAsyncThunk(
    'loyaltyPoints/delete',
    async ({ organizationId, loyaltyPointId, currentIsDeleted }: { organizationId: string; loyaltyPointId: string, currentIsDeleted: boolean }) => {
        try {
            return await LoyaltyPointService.Delete(organizationId, loyaltyPointId, currentIsDeleted);
        } catch (error) {
            return null;
        }
    }
);

// --- State Interface ---
interface LoyaltyPointState {
    loyaltyPoints: ILoyaltyPoint[];
    loading: boolean;
    error: string | null;
}

const initialState: LoyaltyPointState = loadState();

// --- Slice ---
const loyaltyPointSlice = createSlice({
    name: 'loyaltyPoints',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchLoyaltyPoints.pending, (state) => {
                state.loyaltyPoints = [];
                state.loading = true;
                state.error = null;
                saveState(state);
            })
            .addCase(fetchLoyaltyPoints.fulfilled, (state, action) => {
                state.loading = false;
                state.loyaltyPoints = action.payload ?? [];
                saveState(state);
            })
            .addCase(fetchLoyaltyPoints.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                saveState(state);
            })
            .addCase(createLoyaltyPointAsync.fulfilled, (state, action) => {
                if (action.payload) {
                    state.loyaltyPoints.unshift(action.payload);
                    saveState(state);
                }
            })
            .addCase(updateLoyaltyPointAsync.fulfilled, (state, action) => {
                if (action.payload) {
                    const index = state.loyaltyPoints.findIndex(sp => sp.loyaltyPointId === action.payload?.loyaltyPointId);
                    if (index !== -1) {
                        state.loyaltyPoints[index] = action.payload;
                        saveState(state);
                    }
                }
            })
            .addCase(deleteLoyaltyPointAsync.fulfilled, (state, action) => {
                state.loyaltyPoints = state.loyaltyPoints.filter(sp => sp.loyaltyPointId !== action.payload?.loyaltyPointId);
                saveState(state);
            });
    }
});

export default loyaltyPointSlice.reducer;
export const selectLoyaltyPoints = (state: RootState) => state.loyaltyPoints.loyaltyPoints;
export const selectLoyaltyPointsLoading = (state: RootState) => state.loyaltyPoints.loading;
export const selectLoyaltyPointsError = (state: RootState) => state.loyaltyPoints.error;